// Visibility rules of the Saturation Curves section, extracted so SaturationCurves.vue and the
// views that mount it (Model Report, plan details) read the same source. The Admin tab's list of
// hidden sections evaluates these without the component being mounted — the section lives in a
// tab that is unmounted while Admin is open — so the rules cannot live in the component itself.
import { regionsStrictlyIncreasing } from '@/utils/regions.js'
import { hasSaturationDatapoints } from '@/utils/saturationDatapoints.js'

// Grouped saturation carries a first-level 'Instant Marketing' sub-key holding the channels.
export const SATURATION_GROUP_KEY = 'Instant Marketing'

export function hasSaturationGroups (saturationData, enableGroupSelector) {
  return !!(enableGroupSelector && saturationData &&
    Object.prototype.hasOwnProperty.call(saturationData, SATURATION_GROUP_KEY))
}

// The group the component selects on its own: 'Instant Marketing' when present, otherwise the
// first key. null for flat data.
export function defaultSaturationGroup (saturationData, enableGroupSelector) {
  if (!hasSaturationGroups(saturationData, enableGroupSelector)) return null
  const keys = Object.keys(saturationData)
  if (keys.indexOf(SATURATION_GROUP_KEY) !== -1) return SATURATION_GROUP_KEY
  return keys.length ? keys[0] : null
}

// The channel object actually rendered: the selected group, or the flat data.
export function effectiveSaturation (saturationData, group) {
  if (group) {
    return saturationData[group] || {}
  }
  return saturationData
}

// True when the first channel carries zone boundaries — the cheap check the Optimal Zones tab
// and the datapoints switch have always used.
export function hasOptimalZones (effectiveData) {
  const first = effectiveData && Object.keys(effectiveData)[0]
  return !!(first && effectiveData[first].regions)
}

// True when at least one channel has four strictly-increasing boundaries, i.e. zones worth
// shading. Only grouped data acts on this (see optimalZonesMessage below).
export function hasMeaningfulOptimalZones (effectiveData) {
  if (!effectiveData) return false
  const channelsWithRegions = Object.keys(effectiveData)
    .map(function (k) { return effectiveData[k] })
    .filter(function (c) { return c && c.regions && c.regions.length === 4 })
  if (channelsWithRegions.length === 0) return false
  return channelsWithRegions.some(function (c) { return regionsStrictlyIncreasing(c.regions) })
}

export function hasMarginalRoas (effectiveData) {
  const first = effectiveData && Object.keys(effectiveData)[0]
  return !!(first && effectiveData[first].x)
}

// The sub-sections of Saturation Curves that do NOT render, as rows for the Admin tab's
// hidden-sections list. `nameKey` is a full i18n path so both views print the same labels the
// section itself uses; `reason` is a code from HiddenSectionsReport's vocabulary.
// options: { enableGroupSelector, enableImpressionsModel, canExportCurves }
export function hiddenSaturationSections (saturationData, options) {
  const opts = options || {}
  const rows = []
  const grouped = hasSaturationGroups(saturationData, opts.enableGroupSelector)
  const group = defaultSaturationGroup(saturationData, opts.enableGroupSelector)
  const data = effectiveSaturation(saturationData, group)
  const impressions = !!opts.enableImpressionsModel
  const groupNote = group ? ' (group "' + group + '")' : ''
  // No channels at all: every tab is gone for the same reason, so say it once.
  if (!data || Object.keys(data).length === 0) {
    rows.push({
      nameKey: 'components.SaturationCurves.saturationCurves',
      reason: 'missingData',
      condition: 'saturation'
    })
    return rows
  }
  const v = saturationVisibility(saturationData, group, impressions, grouped)
  if (!v.generalView) {
    rows.push({
      nameKey: 'components.SaturationCurves.generalView',
      reason: 'clientSetting',
      condition: 'client.enableImpressionsModel'
    })
  }
  // Impressions models render Optimal Zones outside the tab strip, so its tab being gone is
  // not the content being hidden — only report it when there are no zone boundaries at all.
  if (!v.optimalZones && !impressions) {
    rows.push({
      nameKey: 'components.SaturationCurves.optimalZones',
      reason: 'missingData',
      condition: 'saturation[channel].regions' + groupNote
    })
  }
  if (v.optimalZonesMessage) {
    rows.push({
      nameKey: 'components.SaturationCurves.optimalZones',
      reason: 'notApplicable',
      condition: 'regions not strictly increasing' + groupNote
    })
  }
  if (!v.marginalRoas) {
    rows.push({
      nameKey: 'components.SaturationCurves.marginalRoas',
      reason: impressions ? 'clientSetting' : 'missingData',
      condition: impressions ? 'client.enableImpressionsModel' : 'saturation[channel].x' + groupNote
    })
  }
  if (opts.enableGroupSelector && !grouped) {
    rows.push({
      nameKey: 'components.SaturationCurves.groupSelector',
      reason: 'missingData',
      condition: "saturation['" + SATURATION_GROUP_KEY + "']"
    })
  }
  if (!v.periodCollapse) {
    rows.push({
      nameKey: 'components.SaturationCurves.periodCollapseSelector',
      reason: 'missingData',
      condition: 'saturation[channel].datapoints + datapoints_dates' + groupNote
    })
  }
  if (!opts.canExportCurves) {
    rows.push({
      nameKey: 'components.SaturationCurves.exportCurves',
      reason: 'role',
      condition: 'can-export-curves'
    })
  }
  return rows
}

// Which sub-sections of Saturation Curves render, for a given group selection.
// `grouped` is needed because the "no optimal zones" message is only shown for grouped data;
// flat data keeps its previous behavior of drawing the charts with no zone shading.
export function saturationVisibility (saturationData, group, enableImpressionsModel, grouped) {
  const data = effectiveSaturation(saturationData, group)
  const impressions = !!enableImpressionsModel
  const zones = hasOptimalZones(data)
  const message = !!grouped && !hasMeaningfulOptimalZones(data)
  return {
    // The two first tabs are hidden for impressions models, which render Optimal Zones
    // outside the tab strip instead.
    generalView: !impressions,
    optimalZones: zones && !impressions,
    marginalRoas: hasMarginalRoas(data) && !impressions,
    // Per-channel Optimal Zones charts, whether inside the tab or standalone.
    optimalZonesCharts: !message && (zones || impressions),
    optimalZonesMessage: message,
    // The one datapoints control sits on the tab strip and governs every tab, so it is shown
    // wherever there are datapoints to draw — impressions models included, which render
    // Optimal Zones (and its points) outside the tabs.
    periodCollapse: hasSaturationDatapoints(data)
  }
}
